import {
  PROXY,
  applicantSection,
  businessSection,
  otherAttachments,
  proxyAttachments,
  replySection,
} from "modules/ebiForms/common";
import { businessRepresentationSection } from "modules/ebiForms/definitions/sharedMunicipalServices";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B8. Zgłoszenie do ewidencji innych obiektów świadczących usługi hotelarskie — art. 38 ust. 3 i art. 39 ust. 3 ustawy
 * z 29.08.1997 r. o usługach hotelarskich oraz usługach pilotów wycieczek i przewodników turystycznych; § 12 ust. 3,
 * § 16 ust. 2 (treść karty: przedsiębiorca, nazwa i adres obiektu, charakter stały/sezonowy, miejsca noclegowe),
 * § 17 (zmiany i zaprzestanie) i zał. nr 7 (minimalne wymagania wyposażenia) rozporządzenia MGiP z 19.08.2004 r.
 * Warianty z katalogu: wpis / zmiana danych / zaprzestanie. Wzór elektroniczny ministra (art. 39 ust. 6) realizuje
 * e-usługa biznes.gov.pl ou1130; układ pól jak wzór Gminy Jasło pobrany 2026-10-22 (dane wnioskodawcy, NIP i KRS,
 * dane i opis obiektu, pokoje i miejsca noclegowe, oświadczenia, deklaracja wymagań zał. nr 7) —
 * https://samorzad.gov.pl/attachment/141f0624-d00a-4ea0-8501-ed721db249b7. Tabela miejsc wg wielkości pokoi
 * uproszczona do sum; opis obiektu (tabela zał. nr 7) — praktyka urzędów, nieobowiązkowo.
 */
const ENTRY: EbiCondition = { field: "rodzajZgloszenia", equals: "wpis" };
const CHANGE: EbiCondition = { field: "rodzajZgloszenia", equals: "zmiana" };
const CLOSURE: EbiCondition = { field: "rodzajZgloszenia", equals: "zaprzestanie" };
const BUSINESS: EbiCondition = { field: "swiadczacy", equals: "przedsiebiorca" };
const FARMER: EbiCondition = { field: "swiadczacy", equals: "rolnik" };
const FARMER_BY_PROXY: EbiCondition = { all: [FARMER, PROXY] };
const SEASONAL: EbiCondition = { all: [{ not: CLOSURE }, { field: "charakter", equals: "sezonowy" }] };
const CERTIFICATE: EbiCondition = { all: [{ not: CLOSURE }, { field: "zaswiadczenie", equals: true }] };

const B8: IEbiFormDefinition = {
  code: "B8",
  version: 1,
  title: "Zgłoszenie do ewidencji innych obiektów świadczących usługi hotelarskie",
  category: EEbiFormCategory.Permits,
  office: EEbiFormOffice.Municipality,
  documentType: "Pismo",
  description:
    "Wpis kwatery, pokoi gościnnych, agroturystyki, apartamentu lub domków na wynajem do gminnej ewidencji, zmiana danych albo zaprzestanie działalności.",
  keywords: ["ewidencja", "obiekt hotelarski", "noclegi", "kwatera", "agroturystyka", "pokoje gościnne", "apartament", "wynajem krótkoterminowy"],
  organ: "Wójt / Burmistrz / Prezydent Miasta właściwy ze względu na położenie obiektu",
  legalBasis:
    "art. 38 ust. 3 i art. 39 ust. 3 ustawy z dnia 29 sierpnia 1997 r. o usługach hotelarskich oraz usługach pilotów wycieczek i przewodników turystycznych; § 12 ust. 3, § 16 ust. 2 i § 17 rozporządzenia Ministra Gospodarki i Pracy z dnia 19 sierpnia 2004 r. w sprawie obiektów hotelarskich i innych obiektów, w których są świadczone usługi hotelarskie",
  request:
    "Zgłaszam obiekt, w którym są świadczone usługi hotelarskie, do ewidencji innych obiektów (albo zmianę danych objętych ewidencją lub zaprzestanie świadczenia usług) — zgodnie z rodzajem zgłoszenia wskazanym poniżej.",
  feeLabel: "bez opłaty",
  fee: "Wpis do ewidencji — bez opłaty; zaświadczenie o wpisie — 17 zł opłaty skarbowej; pełnomocnictwo — 17 zł.",
  deadlineLabel: "bez decyzji",
  deadline: "Zgłoszenia dokonuje się przed rozpoczęciem świadczenia usług (art. 39 ust. 3). Wpis jest czynnością materialno-techniczną — bez decyzji.",
  variantField: "rodzajZgloszenia",
  variants: {
    wpis: { title: "Zgłoszenie do ewidencji innych obiektów świadczących usługi hotelarskie", documentType: "Pismo" },
    zmiana: { title: "Zgłoszenie zmiany danych w ewidencji innych obiektów świadczących usługi hotelarskie", documentType: "Pismo" },
    zaprzestanie: { title: "Zgłoszenie zaprzestania świadczenia usług hotelarskich w obiekcie objętym ewidencją", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj zgłoszenia",
      fields: [
        {
          code: "rodzajZgloszenia",
          type: "radio",
          label: "Zgłaszam",
          required: true,
          defaultValue: "wpis",
          options: [
            { value: "wpis", label: "obiekt do ewidencji innych obiektów świadczących usługi hotelarskie (przed rozpoczęciem świadczenia usług)" },
            { value: "zmiana", label: "zmianę danych obiektu objętego ewidencją" },
            { value: "zaprzestanie", label: "zaprzestanie świadczenia usług hotelarskich w obiekcie" },
          ],
        },
        {
          code: "numerEwidencyjny",
          type: "text",
          label: "Numer obiektu w ewidencji (jeśli znany)",
          visibleWhen: { not: ENTRY },
          maxLength: 50,
        },
      ],
    },
    applicantSection(),
    businessRepresentationSection({
      party: "przedsiębiorcy albo rolnika",
      self: "jako przedsiębiorca, rolnik albo osoba uprawniona do reprezentacji przedsiębiorcy (np. wspólnik, członek zarządu według KRS)",
    }),
    {
      code: "swiadczacyUslugi",
      title: "Świadczący usługi hotelarskie",
      fields: [
        {
          code: "swiadczacy",
          type: "radio",
          label: "Usługi hotelarskie w obiekcie świadczy",
          required: true,
          defaultValue: "przedsiebiorca",
          options: [
            { value: "przedsiebiorca", label: "przedsiębiorca (wpisany do CEIDG albo KRS)" },
            { value: "rolnik", label: "rolnik wynajmujący pokoje lub miejsca na ustawienie namiotów w gospodarstwie rolnym" },
          ],
        },
        { code: "rolnikNazwa", type: "text", label: "Imię i nazwisko rolnika", required: true, visibleWhen: FARMER_BY_PROXY, maxLength: 300 },
        { code: "rolnikAdres", type: "text", label: "Adres zamieszkania rolnika (ulica, numer, kod pocztowy, miejscowość)", required: true, visibleWhen: FARMER_BY_PROXY, maxLength: 300 },
        { code: "rolnikNip", type: "nip", label: "NIP rolnika (o ile został nadany)", visibleWhen: FARMER, width: "half" },
      ],
    },
    businessSection("Przedsiębiorca świadczący usługi hotelarskie", BUSINESS),
    {
      code: "obiekt",
      title: "Dane obiektu",
      fields: [
        { code: "obiektNazwa", type: "text", label: "Nazwa własna obiektu", required: true, maxLength: 300 },
        { code: "obiektAdres", type: "text", label: "Adres obiektu — ulica, numer", required: true, maxLength: 200 },
        { code: "obiektKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
        { code: "obiektMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
        {
          code: "rodzajObiektu",
          type: "select",
          label: "Rodzaj obiektu",
          required: true,
          visibleWhen: { not: CLOSURE },
          options: [
            { value: "pokojeGoscinne", label: "pokoje gościnne, kwatera prywatna" },
            { value: "agroturystyka", label: "kwatera agroturystyczna (pokoje w gospodarstwie rolnym)" },
            { value: "apartament", label: "apartament lub mieszkanie na wynajem krótkoterminowy" },
            { value: "domki", label: "domki turystyczne" },
            { value: "namioty", label: "miejsca na ustawienie namiotów i przyczep samochodowych" },
            { value: "inny", label: "inny obiekt" },
          ],
        },
        {
          code: "rodzajObiektuInny",
          type: "text",
          label: "Rodzaj obiektu (inny)",
          required: true,
          visibleWhen: { all: [{ not: CLOSURE }, { field: "rodzajObiektu", equals: "inny" }] },
          maxLength: 200,
        },
        { code: "obiektTelefon", type: "phone", label: "Telefon obiektu", visibleWhen: { not: CLOSURE }, width: "half" },
        { code: "obiektEmail", type: "email", label: "Adres e-mail obiektu", visibleWhen: { not: CLOSURE }, width: "half" },
        { code: "obiektWww", type: "text", label: "Adres strony internetowej", visibleWhen: { not: CLOSURE }, maxLength: 200 },
      ],
    },
    {
      code: "miejsca",
      title: "Pokoje i miejsca noclegowe oraz charakter świadczonych usług",
      visibleWhen: { not: CLOSURE },
      fields: [
        {
          code: "charakter",
          type: "radio",
          label: "Usługi świadczone",
          required: true,
          defaultValue: "caloroczny",
          options: [
            { value: "caloroczny", label: "przez cały rok (stały charakter)" },
            { value: "sezonowy", label: "sezonowo" },
          ],
        },
        { code: "sezon", type: "text", label: "Okres sezonu (od — do)", required: true, visibleWhen: SEASONAL, maxLength: 100, placeholder: "np. od 1 maja do 30 września" },
        { code: "liczbaPokoi", type: "number", label: "Liczba pokoi ogółem", requiredWhen: ENTRY, min: 0, max: 9999, width: "half" },
        { code: "pokojeZLazienka", type: "number", label: "w tym z łazienką i WC", min: 0, max: 9999, width: "half" },
        { code: "liczbaMiejsc", type: "number", label: "Liczba miejsc noclegowych ogółem", requiredWhen: ENTRY, min: 0, max: 99999, width: "half" },
        { code: "liczbaApartamentow", type: "number", label: "Liczba apartamentów lub studiów", min: 0, max: 9999, width: "half" },
        { code: "miejscaDomki", type: "number", label: "Miejsca noclegowe w domkach turystycznych", min: 0, max: 99999, width: "half" },
        { code: "miejscaNamioty", type: "number", label: "Miejsca na ustawienie namiotów i przyczep samochodowych", min: 0, max: 99999, width: "half" },
      ],
    },
    {
      code: "zmiana",
      title: "Zmiana danych",
      visibleWhen: CHANGE,
      fields: [
        { code: "opisZmiany", type: "textarea", label: "Jakie dane się zmieniły (dotychczasowe i nowe brzmienie)", required: true, maxLength: 3000 },
        { code: "dataZmiany", type: "date", label: "Data zmiany", width: "half" },
      ],
    },
    {
      code: "zaprzestanie",
      title: "Zaprzestanie świadczenia usług",
      visibleWhen: CLOSURE,
      fields: [{ code: "dataZaprzestania", type: "date", label: "Data zaprzestania świadczenia usług hotelarskich", required: true, width: "half" }],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswWymagania",
          type: "declaration",
          label: "Oświadczam, że zgłaszany do ewidencji obiekt spełnia wymagania budowlane, sanitarne i przeciwpożarowe, niezbędne do prowadzenia usług hotelarskich.",
          required: true,
          visibleWhen: { not: CLOSURE },
        },
        {
          code: "oswWyposazenie",
          type: "declaration",
          label:
            "Oświadczam, że obiekt spełnia minimalne wymagania co do wyposażenia określone w załączniku nr 7 do rozporządzenia Ministra Gospodarki i Pracy z dnia 19 sierpnia 2004 r. w sprawie obiektów hotelarskich i innych obiektów, w których są świadczone usługi hotelarskie.",
          required: true,
          visibleWhen: { not: CLOSURE },
        },
        {
          code: "oswZmiany",
          type: "declaration",
          label: "Zobowiązuję się przesyłać pisemne informacje o zmianach zgodnie z § 17 rozporządzenia Ministra Gospodarki i Pracy z dnia 19 sierpnia 2004 r.",
          required: true,
          visibleWhen: ENTRY,
        },
        {
          code: "oswDane",
          type: "declaration",
          label: "Oświadczam, że wszystkie dane w zgłoszeniu i załączonych dokumentach są zgodne ze stanem faktycznym na dzień jego sporządzenia.",
          required: true,
        },
      ],
    },
    {
      code: "zaswiadczenieSekcja",
      title: "Zaświadczenie",
      visibleWhen: { not: CLOSURE },
      fields: [
        {
          code: "zaswiadczenie",
          type: "checkbox",
          label: "Proszę także o wydanie zaświadczenia o wpisie do ewidencji (opłata skarbowa 17 zł)",
        },
        { code: "kwotaOplaty", type: "fee", label: "Opłata skarbowa za zaświadczenie", feeAmount: 17, visibleWhen: CERTIFICATE },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "opisObiektu",
      label: "Opis obiektu albo tabela spełniania minimalnych wymagań wyposażenia (załącznik nr 7 do rozporządzenia)",
      hint: "Nieobowiązkowo — część urzędów o nią prosi (praktyka urzędów).",
      visibleWhen: ENTRY,
    },
    {
      code: "oplataZaswiadczenie",
      label: "Dowód zapłaty opłaty skarbowej za zaświadczenie (17 zł)",
      visibleWhen: CERTIFICATE,
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default B8;
